import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditCard } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import Patch from "@shared/models/patch-updater"
import { find, get } from "lodash-es"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

interface Payload {
  value: boolean
  cardStackId: string
  sectionId: string
  originalValue: boolean
}

export const LESSON_CARD_MARK_PUBLIC = {
  name: "LESSON_CARD_MARK_PUBLIC",

  patches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: payload.sectionId,
    })
      .set("embedded", "attributes.isPublic", payload.value)
      // This needs to be a document set
      .set("attributes.sectionsArePristine", false).patch

    return [patch]
  },

  undoPatches(payload: Payload) {
    let patch = Patch.create("card-stack", payload.cardStackId, "attributes.cards", {
      id: payload.sectionId,
    })
      .set("attributes.isPublic", payload.originalValue)
      // This needs to be a document set
      .set("attributes.sectionsArePristine", payload.originalValue).patch

    return [patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultLessonModels(payload, findFn, userId)
    let card = find(get(models, "cardStack.attributes.cards"), {
      id: payload.sectionId,
    })
    let cardTitle = get(card, "title")
    let showHidden = payload.value === true ? "Showed" : "Hide"
    let context = defaultLessonContext(payload, models)
    let newContext = Object.assign({}, context, {
      cardTitle: cardTitle,
      showOrHide: showHidden,
    })
    let newEvent: LessonEditCard = {
      ...createBlankEvent(),
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id: payload.cardStackId,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      primary_object_specifier: "cards",
      adverbial_verb: "editing",
      adverbial_object_type: "card",
      adverbial_object_specifier: "public",
      adverbial_object_id: payload.sectionId,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
    }

    return {
      title: `${showHidden} Lesson Card on Class Website`,
      titleForAnalytics: "Lesson Card - Toggle Public",
      description: `${showHidden} ${cardTitle}`,
      context: newContext,
      activity: defaultLessonActivity(payload, models),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default LESSON_CARD_MARK_PUBLIC
